import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import HornListenComponent from './HornListenComponent.jsx';
import HornTalkComponent from './HornTalkComponent.jsx';

const HORN_STATE = {
    PENDING: 'pending',
    READY: 'ready',
    ERROR: 'error',
};

const getCsrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

/**
 * Prepare a page revision and grants, connect the silent WHIP publisher, then
 * activate the device once. Listen remains explicit and hornstate-dependent.
 */
const HornAudioPanel = ({
    alarmId,
    hornId,
    twoWay,
    startUrl,
    intentUrl,
    stopUrl,
    grantStreamsUrl,
    streamStatusUrl,
    userId,
}) => {
    const [panelState, setPanelState] = useState(HORN_STATE.PENDING);
    const [errorMsg, setErrorMsg] = useState('');
    const [revision, setRevision] = useState(null);
    const [hornState, setHornState] = useState(null);
    const [knownToHornStream, setKnownToHornStream] = useState('');
    const [talkStatus, setTalkStatus] = useState(null);
    const [listenStatus, setListenStatus] = useState(null);
    const [reconnectPhase, setReconnectPhase] = useState('idle');
    const [reconnectError, setReconnectError] = useState('');
    const mountedRef = useRef(true);
    const closePublisherRef = useRef(null);
    const activateRef = useRef(null);
    const intentRef = useRef(null);
    const reconnectRef = useRef(null);
    const resetIntentRef = useRef(null);

    const log = useCallback((event, details = {}) => {
        console.info('[HornAudio:Panel]', event, { alarmId, hornId, ...details });
    }, [alarmId, hornId]);

    const publisherReady = useCallback(() => activateRef.current?.(), []);
    const reconnect = useCallback(() => reconnectRef.current?.run(), []);
    const requestIntent = useCallback(() => {
        if (!intentRef.current) throw new Error('Horn audio session is not ready.');
        return intentRef.current();
    }, []);

    useEffect(() => {
        let active = true;
        let pageRevision = null;
        let stopPromise = null;
        let stopRequestSent = false;
        let activationPromise = null;
        let intentPromise = null;
        let reconnecting = false;
        let waitingForHorn = false;
        let receivedReconnectState = false;
        let reconnectTimer;
        mountedRef.current = true;
        setRevision(null);
        setHornState(null);
        setKnownToHornStream('');
        setPanelState(HORN_STATE.PENDING);
        setReconnectPhase('idle');
        setReconnectError('');
        setTalkStatus(null);
        setListenStatus(null);
        const post = (url, body, keepalive = false) => fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken() },
            body: JSON.stringify(body),
            credentials: 'include',
            keepalive,
            signal: keepalive ? undefined : AbortSignal.timeout(15000),
        });
        const closePublisher = () => {
            const publisher = closePublisherRef.current;
            return publisher?.revision === pageRevision ? publisher.close() : undefined;
        };
        const postStop = () => {
            if (stopRequestSent) return;
            stopRequestSent = true;
            return post(stopUrl, { revision: pageRevision }, true)
                .then((response) => log('session:stop-response', { revision: pageRevision, status: response.status }))
                .catch((error) => log('session:stop-failed', { revision: pageRevision, error: error.message }));
        };
        const stop = (unloading = false) => {
            if (!pageRevision) return;
            if (unloading) {
                // Queue audio_stop immediately: an unload cannot wait for WHIP DELETE.
                // All cleanup stays scoped to this page's revision.
                try { Promise.resolve(closePublisher()).catch(() => {}); } catch {}
                if (stopRequestSent) return stopPromise;
                const body = new FormData();
                body.append('_token', getCsrfToken());
                body.append('revision', pageRevision);
                let queued = false;
                try { queued = navigator.sendBeacon?.(stopUrl, body) === true; } catch {}
                log('session:unload-stop', { revision: pageRevision, transport: queued ? 'beacon' : 'fetch-keepalive' });
                if (queued) {
                    stopRequestSent = true;
                    return;
                }
                return postStop();
            }
            if (!stopPromise) {
                log('session:stopping', { revision: pageRevision });
                // Delete the WHIP resource while its revision is still authorized.
                stopPromise = Promise.resolve(closePublisher()).catch(() => {}).then(postStop);
            }
            return stopPromise;
        };
        const fail = (message) => {
            if (!active) return;
            active = false;
            mountedRef.current = false;
            log('session:failed', { revision: pageRevision, error: message });
            setErrorMsg(message);
            setPanelState(HORN_STATE.ERROR);
            stop();
        };
        activateRef.current = () => {
            if (!active || !pageRevision) return;
            if (!activationPromise) {
                activationPromise = (async () => {
                    for (let attempt = 0; attempt < 3 && active; attempt++) {
                        try {
                            log('device:activating', { revision: pageRevision, attempt: attempt + 1 });
                            const response = await post(startUrl, { revision: pageRevision });
                            if (!response.ok) throw new Error('Device audio activation failed (' + response.status + ').');
                            log('device:activated', { revision: pageRevision, status: response.status });
                            return;
                        } catch (error) {
                            log('device:activation-failed', { revision: pageRevision, attempt: attempt + 1, error: error.message });
                            if (attempt === 2) { fail(error.message); return; }
                            await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
                        }
                    }
                })();
            }
            return activationPromise;
        };
        intentRef.current = () => {
            if (!active || !pageRevision || stopPromise || stopRequestSent || reconnecting) throw new Error('Horn audio session is not ready.');
            intentPromise = (async () => {
                await activateRef.current();
                if (!active || stopPromise || stopRequestSent || reconnecting) throw new Error('Horn audio session is not ready.');
                log('device:intent-requesting', { revision: pageRevision });
                const response = await post(intentUrl, { revision: pageRevision });
                if (!response.ok) {
                    log('device:intent-failed', { revision: pageRevision, status: response.status });
                    throw new Error('Could not activate the microphone (' + response.status + '). Click to retry.');
                }
                log('device:intent-sent', { revision: pageRevision });
            })();
            return intentPromise;
        };
        const finishReconnect = (message = '') => {
            clearTimeout(reconnectTimer);
            reconnecting = false;
            waitingForHorn = false;
            if (!active) return;
            setReconnectPhase('idle');
            setReconnectError(message);
        };
        reconnectRef.current = {
            onHornState: () => {
                receivedReconnectState = true;
                if (waitingForHorn || !reconnecting) finishReconnect();
            },
            run: async () => {
                if (!active || !pageRevision || stopPromise || stopRequestSent || reconnecting) return;
                reconnecting = true;
                receivedReconnectState = false;
                setReconnectPhase('sending');
                setReconnectError('');
                // Only this explicit click resets PTT. A stopped hornstate alone
                // never changes capture or the browser's persistent publisher.
                if (resetIntentRef.current?.revision === pageRevision) resetIntentRef.current.reset();
                log('device:reconnect-requesting', { revision: pageRevision });
                try {
                    // Complete any older intent before resetting its backend flag.
                    await intentPromise?.catch(() => {});
                    if (!active || stopPromise || stopRequestSent) return;
                    const response = await post(startUrl, { revision: pageRevision, reconnect: true });
                    if (!response.ok) throw new Error('Could not restart horn audio (' + response.status + ').');
                    const data = await response.json();
                    if (data.revision !== pageRevision) throw new Error('Horn reconnect returned a different revision.');
                    if (!active) return;
                    log('device:reconnect-start-sent', { revision: pageRevision });
                    activationPromise = Promise.resolve();
                    if (receivedReconnectState) { finishReconnect(); return; }
                    waitingForHorn = true;
                    setReconnectPhase('waiting');
                    reconnectTimer = setTimeout(() => {
                        log('device:reconnect-status-timeout', { revision: pageRevision });
                        finishReconnect('Start was sent, but no updated horn status was received. You can try again.');
                    }, 15000);
                } catch (error) {
                    log('device:reconnect-failed', { revision: pageRevision, error: error.message });
                    finishReconnect(error.message);
                }
            },
        };
        const prepare = async () => {
            try {
                log('session:preparing');
                const response = await post(startUrl, { prepare: true });
                const data = await response.json();
                if (!response.ok) throw new Error(data.error || 'Could not prepare horn audio (' + response.status + ').');
                pageRevision = data.revision;
                log('session:authorized', { revision: pageRevision, toHornStream: data.to_horn_stream, fromHornStream: data.from_horn_stream });
                if (!active) { stop(); return; }
                setRevision(pageRevision);
                setKnownToHornStream(data.to_horn_stream || '');
                setPanelState(HORN_STATE.READY);
            } catch (error) {
                fail(error.message || 'Could not prepare horn audio.');
            }
        };
        const renew = async () => {
            if (!active || !pageRevision || stopPromise) return;
            try {
                const response = await post(grantStreamsUrl, { revision: pageRevision });
                log('session:heartbeat', { revision: pageRevision, status: response.status });
                if ([401, 403, 404, 419].includes(response.status)) {
                    fail('Horn audio authorization expired. Reload the alarm page to reconnect.');
                }
            } catch (error) {
                log('session:heartbeat-failed', { revision: pageRevision, error: error.message });
                // A transient network loss is handled by the publisher, using this revision.
            }
        };
        const onVisibility = () => { if (document.visibilityState === 'visible') renew(); };
        const onPageHide = () => { active = false; mountedRef.current = false; stop(true); };
        const onPageShow = (event) => { if (event.persisted) window.location.reload(); };
        const heartbeat = setInterval(renew, 60000);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('beforeunload', onPageHide);
        window.addEventListener('pageshow', onPageShow);
        document.addEventListener('visibilitychange', onVisibility);
        prepare();
        return () => {
            active = false;
            mountedRef.current = false;
            clearInterval(heartbeat);
            clearTimeout(reconnectTimer);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('beforeunload', onPageHide);
            window.removeEventListener('pageshow', onPageShow);
            document.removeEventListener('visibilitychange', onVisibility);
            stop();
        };
    }, [startUrl, intentUrl, stopUrl, grantStreamsUrl, log]);

    // Subscribe to Echo horn-state events once we have a revision.
    useEffect(() => {
        if (!revision || !userId) return;
        if (typeof window === 'undefined' || !window.Echo) return;

        const channelName = 'horn.' + userId;
        const channel = window.Echo.private(channelName);

        const onHornState = (event) => {
            if (!mountedRef.current) return;
            if (event.revision !== revision) return;
            if ((event.alarm_id !== undefined) && (parseInt(event.alarm_id, 10) !== parseInt(alarmId, 10))) return;

            log('device:hornstate', { revision, state: event.state, micReady: event.mic_ready, speakerReady: event.speaker_ready, fromHornStream: event.from_horn_stream });
            reconnectRef.current?.onHornState();
            setHornState(event);
            setPanelState(HORN_STATE.READY);
        };
        channel.listen('.horn-state', onHornState);
        return () => {
            channel.stopListening('.horn-state', onHornState);
        };
    }, [revision, userId, alarmId, log]);

    if (panelState === HORN_STATE.PENDING && !revision) {
        return null;
    }

    if (panelState === HORN_STATE.ERROR) {
        return (
            <Alert severity="error" sx={{ mt: 1 }}>
                Horn audio unavailable: {errorMsg}
            </Alert>
        );
    }

    const isMicReady = hornState?.mic_ready === true;
    const resolvedFromHornStream = hornState?.from_horn_stream || '';
    const resolvedToHornStream = knownToHornStream;

    const showListen = twoWay && isMicReady && resolvedFromHornStream !== '';
    const showTalk = resolvedToHornStream !== '';
    const isReconnecting = reconnectPhase !== 'idle';
    const deviceState = hornState?.state?.toLowerCase();
    const isStopped = deviceState === 'stopped';
    const messages = [];
    if (isReconnecting) {
        messages.push({ id: 'device', severity: 'info', text: reconnectPhase === 'sending' ? 'Sending a new audio start command…' : 'Start sent. Waiting for an updated horn status…', reconnect: true });
    } else if (reconnectError || isStopped) {
        messages.push({ id: 'device', severity: 'warning', text: reconnectError || 'The horn audio session is stopped.', reconnect: true });
    } else if (deviceState === 'error') {
        messages.push({ id: 'device', severity: 'warning', text: 'The horn reported an audio error.' });
    } else if (!hornState) {
        messages.push({ id: 'device', severity: 'info', text: 'Waiting for the horn to connect…' });
    }
    if (showTalk && talkStatus?.error) messages.push({ id: 'talk', severity: 'error', text: 'Talk: ' + talkStatus.error });
    if (showListen && listenStatus?.error) messages.push({ id: 'listen', severity: 'warning', text: 'Listen: ' + listenStatus.error });
    if (showTalk && talkStatus?.needsInteraction && talkStatus.publisherState !== 'failed') {
        messages.push({ id: 'autoplay', severity: 'info', text: 'Your browser paused audio. Enable audio to keep the horn channel active.', enableAudio: true });
    } else if (showTalk && talkStatus?.intentState === 'idle' && talkStatus.publisherState === 'connected' && !talkStatus.error && !isReconnecting) {
        messages.push({ id: 'hint', severity: 'info', text: 'Click the microphone to enable talking, then press and hold to speak.' });
    }
    const severity = messages.some((message) => message.severity === 'error') ? 'error'
        : messages.some((message) => message.severity === 'warning') ? 'warning' : 'info';
    const dismissErrors = () => {
        talkStatus?.dismissError?.();
        listenStatus?.dismissError?.();
        setReconnectError('');
    };

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
                Horn Audio
            </Typography>

            {(showListen || showTalk) && (
                <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
                    {showListen && (
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                            <HornListenComponent
                                fromHornStream={resolvedFromHornStream}
                                embedded
                                onStatusChange={setListenStatus}
                            />
                        </Box>
                    )}
                    {showTalk && (
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                            <HornTalkComponent
                                toHornStream={resolvedToHornStream}
                                revision={revision}
                                onRequestIntent={requestIntent}
                                onPublisherReady={publisherReady}
                                closePublisherRef={closePublisherRef}
                                resetIntentRef={resetIntentRef}
                                disabled={isReconnecting}
                                embedded
                                onStatusChange={setTalkStatus}
                            />
                        </Box>
                    )}
                </Stack>
            )}

            {messages.length > 0 && (
                <Alert
                    severity={severity}
                    onClose={talkStatus?.error || listenStatus?.error || reconnectError ? dismissErrors : undefined}
                    sx={{ mt: 1, py: 0.25, '& .MuiAlert-message': { width: '100%', py: 0.5 } }}
                >
                    <Stack spacing={0.25}>
                        {messages.map((message) => (
                            <Stack key={message.id} direction="row" alignItems="center" spacing={1}>
                                <Typography variant="caption" sx={{ flex: 1 }}>{message.text}</Typography>
                                {message.reconnect && (
                                    <Button size="small" onClick={reconnect} disabled={isReconnecting} sx={{ flexShrink: 0 }}>
                                        {isReconnecting ? <CircularProgress size={14} sx={{ mr: 0.75 }} /> : null}
                                        {isReconnecting ? 'Reconnecting…' : 'Reconnect'}
                                    </Button>
                                )}
                                {message.enableAudio && (
                                    <Button size="small" onClick={talkStatus.enableAudio} sx={{ flexShrink: 0 }}>Enable audio</Button>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </Alert>
            )}

            {hornState && !showListen && !showTalk && (
                <Typography variant="body2" color="text.secondary">
                    Horn connected but audio not yet ready.
                </Typography>
            )}
        </Paper>
    );
};

HornAudioPanel.propTypes = {
    alarmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    hornId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    twoWay: PropTypes.bool,
    startUrl: PropTypes.string.isRequired,
    intentUrl: PropTypes.string.isRequired,
    stopUrl: PropTypes.string.isRequired,
    grantStreamsUrl: PropTypes.string.isRequired,
    streamStatusUrl: PropTypes.string.isRequired,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

HornAudioPanel.defaultProps = {
    twoWay: false,
};

export default HornAudioPanel;
